import React, { PureComponent } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Tabs,
  Tab,
  Hidden,
  FormControl,
  MenuItem
} from '@material-ui/core';
import _ from 'lodash';
import classNames from 'classnames';
import { isMobileOnly } from 'react-device-detect';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import './AccountLimits.scss';
import difference from '../../../utils/difference';
import AppContext from '../../../App.context';
import Button from '../../Button/Button';

interface AccountLimitsProps {
  t: WithTranslation['t'],
  limits: {
    depositLimitDay: {
      value: number,
      next?: number,
      when?:any
    },
    depositLimitMonth: {
      value: number,
      next?: number,
      when?:any
    },
    bookmakerDepositLimitDay: number,
    bookmakerDepositLimitMonth: number,
    totalDepositedDaily: number,
    totalDepositedMonthly: number,
    stakeLimitDay: {
      value: number,
      next?: number,
      when?:any
    },
    stakeLimitMonth: {
      value: number,
      next?: number,
      when?:any
    },
    timeframe: string,
    isBlocked: boolean,
    statistics: {},
    reasonCode: number
  },
  getLimits: () => void,
  getRestrictions: () => void,
  updateLimits: (obj: { // eslint-disable-line
    depositLimitDay: number,
    depositLimitMonth: number,
    stakeLimitDay: number,
    stakeLimitMonth: number,
    password: string }) => void,
  updateRestrictions: (obj: { // eslint-disable-line
    timeframe: number | string,
    disable: boolean,
    password: string,
    reasonCode: string,
    reasonText: string
  }) => void,
  activeWallet: { currency?: number },
  customer: {
    depositLimitDay: number,
    depositLimitMonth: number,
    bookmakerDepositLimitDay: number,
    bookmakerDepositLimitMonth: number,
    totalDepositedDaily: number,
    totalDepositedMonthly: number,
    stakeLimitDay: number,
    stakeLimitMonth: number,
    timeframe: string,
    isBlocked: boolean,
    statistics: {},
    reasonCode: number
  },
  getRestrictionReasons: () => void,
  restrictionReasons: {},
  selectedTimezone: string
}

interface AccountLimitState {
  depositLimitDay?: number,
  depositLimitMonth?: number,
  stakeLimitDay?: number,
  stakeLimitMonth?: number,
  passwordControl?: string,
  passwordRestrictions?: string,
  timeframe?: number | string,
  isBlocked?: boolean,
  selectedTab?: number,
  reasonCode?: string,
  freeTextReason?: string
}

class AccountLimits extends PureComponent<AccountLimitsProps, AccountLimitState> {
  private tabs;
  private reasonInputRef;

  constructor(props: AccountLimitsProps) {
    super(props);

    this.state = {
      depositLimitDay: 0.00,
      depositLimitMonth: 0.00,
      stakeLimitDay: 0.00,
      stakeLimitMonth: 0.00,
      passwordControl: '',
      passwordRestrictions: '',
      timeframe: 'none',
      isBlocked: false,
      selectedTab: 0,
      reasonCode: '18',
      freeTextReason: ''
    } as AccountLimitState;

    this.tabs = React.createRef();
    this.reasonInputRef = React.createRef();
  }

  async componentDidMount() {
    await this.props.getLimits();
    await this.props.getRestrictions();
    await this.props.getRestrictionReasons();

    const diff = difference(this.props.limits, this.state);
    if (diff) {
      const { limits } = this.props;

      this.setState({ ...diff,
        depositLimitDay: limits.depositLimitDay.value,
        depositLimitMonth: limits.depositLimitMonth.value,
        stakeLimitDay:limits.stakeLimitDay.value,
        stakeLimitMonth:limits.stakeLimitMonth.value
      });
    }

    // Workaround to show tab underline for first tab
    setTimeout(() => {
      if (this.tabs.current) {
        this.tabs.current.updateIndicator();
      }
    }, 100);
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(this.props, prevProps)) {
      const diff = difference(this.props.limits, this.state);
      if (diff) {
        const { limits } = this.props;
        this.setState({ ...diff,
          depositLimitDay: limits.depositLimitDay.value,
          depositLimitMonth: limits.depositLimitMonth.value,
          stakeLimitDay:limits.stakeLimitDay.value,
          stakeLimitMonth:limits.stakeLimitMonth.value
        });
      }
    }
  }

  onChange = e => {
    const { name, type, value, checked } = e.target;
    this.setState({ [name]: type === 'checkbox' ? checked : value });
  }

  onTabChange = (event, value) => {
    this.setState({ selectedTab: value });
  }

  submitLimits = (e) => {
    e.preventDefault();

    const {
      depositLimitDay,
      depositLimitMonth,
      stakeLimitDay,
      stakeLimitMonth,
      passwordControl
    } = this.state;

    this.props.updateLimits({
      depositLimitDay: Number(depositLimitDay),
      depositLimitMonth: Number(depositLimitMonth),
      stakeLimitDay: Number(stakeLimitDay),
      stakeLimitMonth: Number(stakeLimitMonth),
      password: passwordControl
    });
    this.setState({ passwordControl: '' });
  }

  submitRestrictions = (e) => {
    e.preventDefault();

    const {
      timeframe,
      isBlocked,
      passwordRestrictions,
      reasonCode,
      freeTextReason
    } = this.state;

    this.props.updateRestrictions({
      timeframe,
      disable: isBlocked,
      password: passwordRestrictions,
      reasonCode: reasonCode,
      reasonText: freeTextReason
    });
    this.setState({ passwordRestrictions: '' });
  }

  formatDate = (date) => {
    const newDate = utcToZonedTime(date * 1000, this.props.selectedTimezone);
    return format(newDate, 'HH:mm:ss dd:MM:yyyy');
  }

  renderRestrictionsSelect = () => {
    const { isBlocked, reasonCode, timeframe, freeTextReason } = this.state;
    const { t, limits, customer, restrictionReasons } = this.props;

    return (
      !_.isEmpty(restrictionReasons) && (isBlocked || timeframe !== 'none') && (
        <FormControl className="account-limits__restriction-reasons">
          <TextField
            select
            name="reasonCode"
            value={ reasonCode }
            variant="outlined"
            disabled={ limits.isBlocked || customer.isBlocked }
            onChange={ this.onChange }
            className="account-limits__confirmation-area-control--input"
          >
            {
              Object.entries(restrictionReasons).map(([key, value]) => (
                <MenuItem
                  className="account-limits__menu-item"
                  key={ key }
                  value={ key }
                >
                  { value }
                </MenuItem>))
            }
          </TextField>
          {
            reasonCode.toString() === '18' && (
              <TextField
                ref={ this.reasonInputRef }
                className="account-limits__reason-input"
                type="text"
                name="freeTextReason"
                label={ t('accountLimits.reason') }
                value={ freeTextReason }
                variant="outlined"
                onChange={ this.onChange }
                required
                InputLabelProps={ { shrink: true } }
                autoFocus
              />
            )
          }
        </FormControl>
      )
    );
  }

  renderSelfLimitationSection = () => {
    const {
      depositLimitDay,
      depositLimitMonth,
      stakeLimitDay,
      stakeLimitMonth,
      passwordControl
    } = this.state;
    const { t, limits } = this.props;

    return (
      <form onSubmit={ this.submitLimits }>
        <div className="account-limits__header">{ t('accountLimits.selfLimitation') }</div>
        <div className="account-limits__description">{ t('accountLimits.selfLimitationDescription') }</div>

        <div className="account-limits__group">
          <table cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>{ t('accountLimits.depositLimit') }</th>
                <th>{ t('accountLimits.perDay') }</th>
                <th>{ t('accountLimits.perMonth') }</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{ t('accountLimits.overall') } ({ this.props.activeWallet && this.props.activeWallet?.currency })</td>
                <td>
                  <TextField
                    type="number"
                    name="depositLimitDay"
                    value={ depositLimitDay }
                    variant="outlined"
                    InputProps={ { inputProps: { min: 0, step: 0.01 } } }
                    onChange={ this.onChange }
                  />
                  {
                    limits.depositLimitDay.next &&
                    <span className="account-limits__message">{
                      t('accountLimits.nextLimit', {
                        next: limits.depositLimitDay.next, when: this.formatDate(limits.depositLimitDay.when) })
                    }</span>
                  }
                </td>
                <td>
                  <TextField
                    type="number"
                    name="depositLimitMonth"
                    value={ depositLimitMonth }
                    variant="outlined"
                    InputProps={ { inputProps: { min: 0, step: 0.01 } } }
                    onChange={ this.onChange }
                  />
                  {
                    limits.depositLimitMonth.next &&
                    <span className="account-limits__message">{
                      t('accountLimits.nextLimit', {
                        next: limits.depositLimitMonth.next, when: this.formatDate(limits.depositLimitMonth.when) })
                    }</span>
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="account-limits__group account-limits__group--side-right">
          <table cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>{ t('accountLimits.stakeLimit') }</th>
                <th>{ t('accountLimits.perDay') }</th>
                <th>{ t('accountLimits.perMonth') }</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{ t('accountLimits.overall') } ({ this.props.activeWallet && this.props.activeWallet?.currency })</td>
                <td>
                  <TextField
                    type="number"
                    name="stakeLimitDay"
                    value={ stakeLimitDay }
                    variant="outlined"
                    InputProps={ { inputProps: { min: 0, step: 0.01 } } }
                    onChange={ this.onChange }
                  />
                  {
                    limits.stakeLimitDay.next &&
                    <span className="account-limits__message">{
                      t('accountLimits.nextLimit', {
                        next: limits.stakeLimitDay.next, when: this.formatDate(limits.stakeLimitDay.when) })
                    }</span>
                  }
                </td>
                <td>
                  <TextField
                    type="number"
                    name="stakeLimitMonth"
                    value={ stakeLimitMonth }
                    variant="outlined"
                    InputProps={ { inputProps: { min: 0, step: 0.01 } } }
                    onChange={ this.onChange }
                  />
                  {
                    limits.stakeLimitMonth.next &&
                    <span className="account-limits__message">{
                      t('accountLimits.nextLimit', {
                        next: limits.stakeLimitMonth.next, when: this.formatDate(limits.stakeLimitMonth.when) })
                    }</span>
                  }                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="account-limits__confirmation-area">
          {
            !isMobileOnly ?
              <span>{ t('accountLimits.yourPassword') }:</span>
              :
              <span>{ t('accountLimits.yourPasswordMobile') }:</span>
          }
          <TextField
            type="password"
            name="passwordControl"
            value={ passwordControl }
            autoComplete="new-password"
            variant="outlined"
            onChange={ this.onChange }
            required
            className="account-limits__confirmation-area--input"
          />

          { !isMobileOnly && <small>{ t('accountLimits.enterPassword') }</small> }
        </div>

        <Button className="btn--cta" disabled={ passwordControl === '' } text={ t('accountLimits.save') } type = "submit" />
      </form>
    );
  }

  renderSelfControlSection = () => {
    const { passwordRestrictions, timeframe, isBlocked } = this.state;
    const { t, limits, customer } = this.props;

    return (
      <form onSubmit={ this.submitRestrictions }>
        <div className="account-limits__header">{ t('accountLimits.selfControl') }</div>
        <div className="account-limits__description">{ t('accountLimits.selfControlDescription') }</div>

        <div className="account-limits__group">
          <table cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>{ t('accountLimits.coolingOffPeriod') }</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="account-limits__description">{ t('accountLimits.coolingOffPeriodDescription') }</td>
              </tr>
              <tr>
                <td>
                  { !isMobileOnly && <span>{ t('accountLimits.selectTimeframe') }</span> }

                  <RadioGroup row aria-label="position" name="position" defaultValue="top">
                    <div className="account-limits__timeframe-group">
                      <FormControlLabel
                        value="none"
                        className="account-limits__radio"
                        control={
                          <Radio
                            name="timeframe"
                            color="default"
                            checked={ timeframe === 'none' }
                            disabled={ limits.timeframe !== 'none' || limits.isBlocked || customer.isBlocked }
                            onChange={ this.onChange }
                            className="account-limits__radio"
                          />
                        }
                        label={ <span className="account-limits__radio--label">{ t('accountLimits.none') }</span> }
                      />
                      <FormControlLabel
                        value="one_day"
                        className="account-limits__radio"
                        control={
                          <Radio
                            name="timeframe"
                            color="default"
                            checked={ timeframe === 'one_day' }
                            disabled={ limits.timeframe !== 'none' || limits.isBlocked || customer.isBlocked }
                            onChange={ this.onChange }
                            className="account-limits__radio"
                          />
                        }
                        label={ <span className="account-limits__radio-label">{ t('accountLimits.1day') }</span> }
                      />
                    </div>
                    <div className="account-limits__timeframe-group">
                      <FormControlLabel
                        value="four_weeks"
                        className="account-limits__radio"
                        control={
                          <Radio
                            name="timeframe"
                            color="default"
                            checked={ timeframe === 'four_weeks' }
                            disabled={ limits.timeframe !== 'none' || limits.isBlocked || customer.isBlocked }
                            onChange={ this.onChange }
                            className="account-limits__radio"
                          />
                        }
                        label={ <span className="account-limits__radio-label">{ t('accountLimits.4weeks') }</span> }
                      />
                      <FormControlLabel
                        value="six_months"
                        className="account-limits__radio"
                        control={
                          <Radio
                            name="timeframe"
                            color="default"
                            checked={ timeframe === 'six_months' }
                            disabled={ limits.timeframe !== 'none' || limits.isBlocked || customer.isBlocked }
                            onChange={ this.onChange }
                            className="account-limits__radio"
                          />
                        }
                        label={ <span className="account-limits__radio-label">{ t('accountLimits.6months') }</span> }
                      />
                    </div>
                  </RadioGroup>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="account-limits__group account-limits__group--side-right">
          <table cellSpacing="0" cellPadding="0">
            <thead>
              <tr>
                <th>{ t('accountLimits.disableAccount') }</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="account-limits__description">{ t('accountLimits.disableAccountDescription') }</td>
              </tr>
              <tr>
                <td>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isBlocked"
                        color="default"
                        className="account-limits__disable-account"
                        checked={ isBlocked }
                        disabled={ limits.isBlocked || customer.isBlocked }
                        onChange={ this.onChange }
                      />
                    }
                    label={ <span className="account-limits__disable-account--label">{ t('accountLimits.disableMessage') }</span> }
                  />
                </td>
              </tr>
              <tr className="account-limits__restriction-reasons"></tr>
            </tbody>
          </table>
        </div>

        { this.renderRestrictionsSelect() }

        <div className="account-limits__confirmation-area">
          {
            !isMobileOnly ?
              <span>{ t('accountLimits.yourPassword') }:</span>
              :
              <span>{ t('accountLimits.yourPasswordMobile') }:</span>
          }
          <TextField
            type="password"
            name="passwordRestrictions"
            value={ passwordRestrictions }
            autoComplete="new-password"
            variant="outlined"
            onChange={ this.onChange }
            disabled={ ((timeframe !== limits.timeframe) && limits.timeframe !== 'none') || limits.isBlocked || customer.isBlocked }
            required
            className="account-limits__confirmation-area--input"
          />

          { !isMobileOnly && <small>{ t('accountLimits.enterPassword') }</small> }
        </div>

        <Button
          className="btn--cta"
          onClick={ this.submitRestrictions }
          text={ t('accountLimits.save') }
          disabled={
            ((
              (timeframe !== limits.timeframe) && limits.timeframe !== 'none') ||
              limits.isBlocked || customer.isBlocked) || passwordRestrictions === ''
          }
          type = "submit"
        />
      </form>
    );
  }

  render() {
    const { selectedTab } = this.state;
    const { t } = this.props;

    return (
      <div className="account-limits">
        <Hidden mdDown>
          <div className="account-limits__section">
            { this.renderSelfLimitationSection() }
          </div>

          <div className="account-limits__section">
            { this.renderSelfControlSection() }
          </div>
        </Hidden>
        <Hidden lgUp>
          <Tabs
            ref={ this.tabs }
            className="account-limits__tab-container"
            value={ selectedTab }
            onChange={ this.onTabChange }
            action={ this.tabs }
            variant="fullWidth"
          >
            <Tab
              label={ t('accountLimits.selfLimitation') }
              id="simple-tab-0"
              aria-controls="simple-tabpanel-0"
              className={
                classNames({
                  'account-limits__tab-active' : selectedTab === 0,
                  'account-limits__tab-inactive': selectedTab !== 0
                })
              }
            />
            <Tab
              label={ t('accountLimits.selfControl') }
              id="simple-tab-1"
              aria-controls="simple-tabpanel-1"
              className={
                classNames({
                  'account-limits__tab-active' : selectedTab === 1,
                  'account-limits__tab-inactive': selectedTab !== 1
                })
              }
            />
          </Tabs>
          <div className="account-limits__tab-panel">
            { selectedTab === 0 && this.renderSelfLimitationSection() }
            { selectedTab === 1 && this.renderSelfControlSection() }
          </div>
        </Hidden>
      </div>
    );
  }
}

const mapState = state => ({
  customer: state.auth.customer,
  limits: state.account.limits,
  restrictionReasons: state.account.restrictionReasons,
  activeWallet: state.auth.activeWallet,
  selectedTimezone: state.odds.selectedTimezone
});

const mapDispatch = dispatch => ({
  getLimits: () => dispatch.account.getUserLimits(),
  getRestrictions: () => dispatch.account.getUserRestrictions(),
  updateLimits: (obj) => dispatch.account.updateUserLimits(obj),
  updateRestrictions: (obj) => dispatch.account.updateUserRestrictions(obj),
  getRestrictionReasons: () => dispatch.account.getRestrictionReasons()
});

AccountLimits.contextType = AppContext;

export default withTranslation('common')(connect(mapState, mapDispatch)(AccountLimits));
